import { useCallback, useEffect, useState } from 'react';
import { ResponseErrorPanel } from '@mainstage/core-components';
import {
  WbBadge,
  WbCardContent,
  WbDrawer,
  WbDrawerContent,
  isoDateToDisplayString,
  GenericGridItem,
  WbGenericFieldGrid,
  SystemIcon,
} from '@agilelab/plugin-wb-platform';
import {
  FeatureRequestPriority,
  FeatureRequest,
  transformUrnToWitboostId,
} from '@agilelab/plugin-wb-builder-common';
import {
  EntityDisplayName,
  catalogApiRef,
  getEntityRelations,
} from '@mainstage/plugin-catalog-react';
import { RELATION_PART_OF, stringifyEntityRef } from '@mainstage/catalog-model';
import { PART_OF } from '@agilelab/plugin-wb-practice-shaper-common';
import { useApi } from '@mainstage/core-plugin-api';
import { useQuery } from '@tanstack/react-query';
import { useFeatureRequest } from '../../hooks/useFeatureRequest';
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  Divider,
  makeStyles,
  Theme,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Progress } from '@mainstage/core-components';
import { FeatureRequestDeleteDialog } from './FeatureRequestDeleteDialog';

const PRIORITY_COLOR_MAP: Record<
  FeatureRequestPriority,
  'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
> = {
  [FeatureRequestPriority.LOW]: 'info',
  [FeatureRequestPriority.MEDIUM]: 'warning',
  [FeatureRequestPriority.HIGH]: 'error',
  [FeatureRequestPriority.CRITICAL]: 'error',
};

const useStyles = makeStyles((theme: Theme) => ({
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1.5),
    height: 48,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cardHeaderTitle: {
    flex: 1,
    fontWeight: 500,
    fontSize: 12,
    color: theme.palette.text.primary,
  },
  collapseIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  rightSection: {
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    padding: '16px 16px',
    minWidth: '20%',
    display: 'grid',
    [theme.breakpoints.down('sm')]: {
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      borderLeft: 'none',
      paddingLeft: '0',
      paddingRight: '0',
      minWidth: '100%',
    },
  },
  infoIcon: {
    fontSize: 16,
    color: theme.palette.secondary.main,
    flexShrink: 0,
    marginTop: 1,
  },
  attachmentCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    flex: 1,
  },
  attachmentThumbnail: {
    width: 85,
    height: 64,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[300]}`,
    objectFit: 'cover' as const,
    flexShrink: 0,
  },
  attachmentPlaceholder: {
    width: 85,
    height: 64,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    flexShrink: 0,
  },
  attachmentFilename: {
    flex: 1,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attachmentDivider: {
    alignSelf: 'stretch',
    margin: theme.spacing(1.5, 0),
  },
  cardContainer: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
}));

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const IMAGE_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

export interface FeatureRequestDetailProps {
  featureRequestId: string | null;
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
  featureRequest?: FeatureRequest;
  isLoading?: boolean;
  error?: Error | null;
  fetchAttachmentBlob?: (
    featureRequestId: string,
    attachmentId: string,
  ) => Promise<Blob>;
}

export const FeatureRequestDetail = ({
  featureRequestId,
  open,
  onClose,
  readOnly = false,
  featureRequest: externalData,
  isLoading: externalIsLoading,
  error: externalError,
  fetchAttachmentBlob,
}: FeatureRequestDetailProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const internalQuery = useFeatureRequest(
    externalData !== undefined ? '' : featureRequestId ?? '',
  );
  const data = externalData ?? internalQuery.data;
  const isLoading = externalIsLoading ?? internalQuery.isLoading;
  const error =
    externalError !== undefined ? externalError : internalQuery.error;

  const systemRef = data?.projectUrn
    ? transformUrnToWitboostId(data.projectUrn)
    : undefined;

  const { data: domainRef } = useQuery({
    queryKey: ['feature-request-domain', systemRef],
    queryFn: async () => {
      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: [systemRef!],
        fields: ['relations'],
      });
      const entity = items[0];
      if (!entity) return null;
      const partOfDomain = getEntityRelations(entity, RELATION_PART_OF, {
        kind: 'domain',
      });
      const wbPartOfDomain = getEntityRelations(entity, PART_OF, {
        kind: 'domain',
      });
      const domainRelation = partOfDomain[0] ?? wbPartOfDomain[0];
      if (!domainRelation) return null;
      return stringifyEntityRef(domainRelation);
    },
    enabled: !!systemRef,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    if (!data?.attachments || !fetchAttachmentBlob || !featureRequestId) return;

    const imageAttachments = data.attachments.filter(a =>
      IMAGE_CONTENT_TYPES.includes(a.contentType),
    );
    if (imageAttachments.length === 0) return;

    let cancelled = false;
    const urls: string[] = [];

    Promise.all(
      imageAttachments.map(async attachment => {
        const blob = await fetchAttachmentBlob(featureRequestId, attachment.id);
        const url = URL.createObjectURL(blob);
        urls.push(url);
        return { id: attachment.id, url };
      }),
    ).then(results => {
      if (!cancelled) {
        setThumbnailUrls(prev => {
          const next = { ...prev };
          for (const { id, url } of results) {
            next[id] = url;
          }
          return next;
        });
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      urls.forEach(URL.revokeObjectURL);
    };
  }, [data?.attachments, fetchAttachmentBlob, featureRequestId]);

  const handleSetOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) onClose();
    },
    [onClose],
  );

  let content;
  if (error && featureRequestId) {
    content = <ResponseErrorPanel error={error as Error} />;
  } else if (isLoading || !data) {
    content = featureRequestId ? <Progress /> : null;
  } else {
    content = (
      <>
        {/* Request Details Card */}
        <Box className={classes.cardContainer}>
          <Box
            className={classes.cardHeader}
            onClick={() => setDetailsExpanded(prev => !prev)}
            aria-expanded={detailsExpanded}
            aria-label="Toggle request details"
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDetailsExpanded(prev => !prev);
              }
            }}
          >
            {detailsExpanded ? (
              <ExpandLessIcon className={classes.collapseIcon} />
            ) : (
              <ExpandMoreIcon className={classes.collapseIcon} />
            )}
            <Typography className={classes.cardHeaderTitle}>
              Request Details
            </Typography>
          </Box>
          <Collapse in={detailsExpanded}>
            <WbCardContent>
              <Box className={classes.container}>
                <Box
                  display="flex"
                  flexDirection="column"
                  flex="1"
                  paddingRight={2}
                >
                  <WbGenericFieldGrid
                    fields={[
                      {
                        label: 'Requester',
                        value: (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 4 }}
                          >
                            <AccountBoxIcon className={classes.infoIcon} />
                            <EntityDisplayName
                              entityRef={data.requesterRef}
                              defaultKind="user"
                              hideIcon
                            />
                          </Box>
                        ),
                        colSpan: { xs: 4, sm: 2 },
                      } satisfies GenericGridItem,
                      {
                        label: 'Priority',
                        value: (
                          <WbBadge
                            color={PRIORITY_COLOR_MAP[data.priority]}
                            startIcon={<DragHandleIcon fontSize="small" />}
                          >
                            {data.priority}
                          </WbBadge>
                        ),
                        colSpan: { xs: 4, sm: 2 },
                      } satisfies GenericGridItem,
                      {
                        label: 'System',
                        value: (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 4 }}
                          >
                            <SystemIcon className={classes.infoIcon} />
                            {data.projectUrn ? (
                              <EntityDisplayName
                                entityRef={
                                  transformUrnToWitboostId(data.projectUrn) ??
                                  data.projectUrn
                                }
                                defaultKind="system"
                                hideIcon
                              />
                            ) : (
                              '—'
                            )}
                          </Box>
                        ),
                        colSpan: { xs: 4, sm: 2 },
                      } satisfies GenericGridItem,
                      {
                        label: 'Component',
                        value: data.componentUrn ? (
                          <EntityDisplayName
                            entityRef={
                              transformUrnToWitboostId(data.componentUrn) ??
                              data.componentUrn
                            }
                            defaultKind="component"
                            hideIcon
                          />
                        ) : (
                          '—'
                        ),
                        colSpan: { xs: 4, sm: 2 },
                      } satisfies GenericGridItem,
                      {
                        label: 'Domain',
                        icon: 'mui:Domain',
                        value: domainRef ? (
                          <EntityDisplayName
                            entityRef={domainRef}
                            defaultKind="domain"
                            hideIcon
                          />
                        ) : (
                          '—'
                        ),
                        colSpan: { xs: 4, sm: 2 },
                      } satisfies GenericGridItem,
                      {
                        label: 'Request',
                        value: data.description || '—',
                        colSpan: 4,
                      },
                    ]}
                    columns={4}
                    borderBetweenRows
                    borderAfterLastRow={false}
                  />
                </Box>
                <Box className={classes.rightSection}>
                  <WbGenericFieldGrid
                    fields={[
                      {
                        label: 'Created',
                        icon: 'mui:Event',
                        value: isoDateToDisplayString(data.createdAt),
                        colSpan: 2,
                      },
                      {
                        label: 'Last Update',
                        icon: 'mui:EventAvailable',
                        value: isoDateToDisplayString(data.updatedAt),
                        colSpan: 2,
                      },
                    ]}
                    columns={2}
                    borderBetweenRows
                    borderAfterLastRow={false}
                  />
                </Box>
              </Box>
            </WbCardContent>
          </Collapse>
        </Box>

        {/* Attachments Card */}
        <Box mt={1.5}>
          <Box className={classes.cardContainer}>
            <Box
              className={classes.cardHeader}
              onClick={() => setAttachmentsExpanded(prev => !prev)}
              aria-expanded={attachmentsExpanded}
              aria-label="Toggle attachments"
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setAttachmentsExpanded(prev => !prev);
                }
              }}
            >
              {attachmentsExpanded ? (
                <ExpandLessIcon className={classes.collapseIcon} />
              ) : (
                <ExpandMoreIcon className={classes.collapseIcon} />
              )}
              <Typography className={classes.cardHeaderTitle}>
                Attachments
              </Typography>
            </Box>
            <Collapse in={attachmentsExpanded}>
              <Box style={{ backgroundColor: 'white' }}>
                {data.attachments.length === 0 ? (
                  <Box p={1.5}>
                    <Typography variant="body2" color="textSecondary">
                      No attachments
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="flex-start">
                    {data.attachments.map((attachment, index) => (
                      <Box
                        key={attachment.id}
                        display="flex"
                        alignItems="flex-start"
                        style={{ flex: 1 }}
                      >
                        {index > 0 && (
                          <Divider
                            orientation="vertical"
                            flexItem
                            className={classes.attachmentDivider}
                          />
                        )}
                        <Box className={classes.attachmentCell}>
                          {thumbnailUrls[attachment.id] ? (
                            <img
                              className={classes.attachmentThumbnail}
                              src={thumbnailUrls[attachment.id]}
                              alt={attachment.filename}
                            />
                          ) : (
                            <Box className={classes.attachmentPlaceholder}>
                              <AttachFileIcon color="action" />
                            </Box>
                          )}
                          <AttachFileIcon
                            fontSize="small"
                            color="action"
                            style={{ flexShrink: 0 }}
                          />
                          <Typography className={classes.attachmentFilename}>
                            {attachment.filename}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={`Download ${attachment.filename}`}
                            onClick={async () => {
                              if (fetchAttachmentBlob && featureRequestId) {
                                const blob = await fetchAttachmentBlob(
                                  featureRequestId,
                                  attachment.id,
                                );
                                triggerBlobDownload(blob, attachment.filename);
                              }
                            }}
                            disabled={!fetchAttachmentBlob || !featureRequestId}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>

        {!readOnly && (
          <FeatureRequestDeleteDialog
            open={deleteDialogOpen}
            featureRequestId={featureRequestId!}
            summary={data.summary}
            onClose={() => setDeleteDialogOpen(false)}
            onDeleted={onClose}
          />
        )}
      </>
    );
  }

  return (
    <WbDrawer
      open={open}
      setOpen={handleSetOpen}
      title="Feature Request Details"
      size={75}
    >
      <WbDrawerContent>{content}</WbDrawerContent>
    </WbDrawer>
  );
};
